import Papa from 'papaparse';
import { HiBidCSVRow, AuctionLot } from '../types/auction.types';

export interface ParsedHiBidData {
  lots: AuctionLot[];
  imageFolders: Set<string>; // Unique folder names from Notes field
  errors: string[];
}

/**
 * Parse HiBid CSV file and convert to AuctionLot objects
 */
export const parseHiBidCSV = (csvText: string): ParsedHiBidData => {
  const result: ParsedHiBidData = {
    lots: [],
    imageFolders: new Set(),
    errors: [],
  };

  try {
    const parsed = Papa.parse<HiBidCSVRow>(csvText, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (header) => header.trim(),
    });

    if (parsed.errors.length > 0) {
      result.errors = parsed.errors.map(e => `Row ${e.row}: ${e.message}`);
    }

    let rowsRead = 0;
    let skipped = 0;

    parsed.data.forEach((row, index) => {
      rowsRead++;
      // Skip empty rows
      if (!row['Item ID'] || !row['Title']) {
        skipped++;
        return;
      }

      try {
        // Parse both category columns
        const hibidCat = parseCategoryPath(row['eBay Category1Name']);
        const storeCat = parseCategoryPath(row['eBay Store Category1Name']);
        
        const lot: AuctionLot = {
          id: `lot-${row['Item ID']}-${Date.now()}`,
          sku: row['SKU'] || row['Item ID'], // For image matching
          lotNumber: row['Lot Number'] || row['Lot #'] || row['Lot'] || row['SKU'] || row['Item ID'], // For display
          // FIELD MAPPING (was reversed — bug on the inaugural sale):
          //   HiBid 'Title' = the short product name -> lot.title (bold on the tile)
          //   HiBid 'eBay Description' = the long marketing paragraph -> lot.description
          title: row['Title'],
          description: row['eBay Description'] || row['eBay Condition Description'] || row['Title'],
          startingBid: parseStartingBid(row['Starting Price']),
          
          // Categories - prioritize store category, fall back to HiBid category
          category: storeCat !== 'Uncategorized' ? storeCat : hibidCat,
          hibidCategory: hibidCat !== 'Uncategorized' ? hibidCat : undefined,
          storeCategory: storeCat !== 'Uncategorized' ? storeCat : undefined,
          
          condition: extractCondition(row['eBay Condition Description']),
          measures: row['Measures'] || undefined,
          images: [],
          currentBid: parseStartingBid(row['Starting Price']),
          bidCount: 0,
          status: 'pending',
          
          // Tier, when the sheet carries one. Lower-cased so 'Chaser' and 'chaser'
          // sort the same; absent stays absent rather than becoming ''.
          tier: (row['tier'] || '').trim().toLowerCase() || undefined,

          // HiBid metadata
          hibidLotId: row['Item ID'],
          consignor: row['Consignor'],
          notes: row['Notes'], // FOLDER NAME
          
          createdAt: new Date(),
        };

        // Track image folder
        if (row['Notes']) {
          result.imageFolders.add(row['Notes']);
        }

        result.lots.push(lot);
      } catch (error) {
        result.errors.push(`Row ${index + 2}: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    });

    // SAY SO WHEN NOTHING LANDS.
    // A sheet with the wrong column names produces zero lots and zero errors —
    // every row is skipped above without recording anything, so the UI shows a
    // clean screen and no lots, which reads as "the app is broken" rather than
    // "this is the wrong file". That silence cost a full import cycle on the
    // joe104 sale. Report what was read and, crucially, the headers actually
    // found, so the next person can see the mismatch without opening the CSV.
    if (rowsRead > 0 && result.lots.length === 0) {
      const found = (parsed.meta.fields || []).join(', ');
      result.errors.push(
        `${rowsRead} rows read, 0 had both "Item ID" and "Title" — the two required columns. ` +
        `Columns found in this file: ${found || '(none)'}`
      );
    } else if (skipped > 0) {
      result.errors.push(
        `${rowsRead} rows read, ${result.lots.length} imported, ${skipped} skipped for a missing "Item ID" or "Title".`
      );
    }

  } catch (error) {
    result.errors.push(`Parse error: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }

  return result;
};

/**
 * Parse starting bid from string (handles empty, $, commas)
 * Returns value in cents
 */
const parseStartingBid = (priceStr: string): number => {
  if (!priceStr || priceStr.trim() === '') {
    return 0; // Default starting bid
  }
  
  // Remove $, commas, and spaces
  const cleaned = priceStr.replace(/[$,\s]/g, '');
  const dollars = parseFloat(cleaned);
  
  if (isNaN(dollars)) {
    return 0;
  }
  
  // Convert to cents
  return Math.round(dollars * 100);
};

/**
 * Extract category from HiBid path (e.g., "Home Goods & Decor > Lighting > Lamps")
 * Returns 'Uncategorized' if empty or invalid
 */
const parseCategoryPath = (categoryPath: string): string => {
  if (!categoryPath || categoryPath.trim() === '') return 'Uncategorized';
  
  // Take the last part of the path as the main category
  const parts = categoryPath.split('>').map(p => p.trim());
  const category = parts[parts.length - 1] || parts[0] || 'Uncategorized';
  
  // Return Uncategorized if the result is empty
  return category.trim() === '' ? 'Uncategorized' : category;
};

/**
 * Try to extract condition from description (PSA 8, Used, Vintage, etc.)
 */
const extractCondition = (description: string): string | undefined => {
  if (!description) return undefined;
  
  const lowerDesc = description.toLowerCase();
  
  // Check for common condition keywords
  if (lowerDesc.includes('psa ')) {
    const match = description.match(/psa\s+(\d+)/i);
    if (match) return `PSA ${match[1]}`;
  }
  
  if (lowerDesc.includes('mint')) return 'Mint';
  if (lowerDesc.includes('near mint')) return 'Near Mint';
  if (lowerDesc.includes('excellent')) return 'Excellent';
  if (lowerDesc.includes('good')) return 'Good';
  if (lowerDesc.includes('fair')) return 'Fair';
  if (lowerDesc.includes('used')) return 'Used';
  if (lowerDesc.includes('new')) return 'New';
  if (lowerDesc.includes('vintage')) return 'Vintage';
  if (lowerDesc.includes('antique')) return 'Antique';
  
  return undefined;
};

/**
 * Generate expected image filenames based on SKU
 * Example: SKU 700001 → ["700001_00", "700001_01", "700001_02", ...]
 */
export const generateImageFilenames = (sku: string, maxImages: number = 20): string[] => {
  const filenames: string[] = [];
  
  for (let i = 0; i < maxImages; i++) {
    const imageNumber = i.toString().padStart(2, '0');
    filenames.push(`${sku}_${imageNumber}`);
  }
  
  return filenames;
};

/**
 * Match uploaded images to lots based on folder name and SKU
 */
export interface ImageMatcher {
  folder: string; // From Notes field
  sku: string;
  expectedFilenames: string[];
}

export const createImageMatchers = (lots: AuctionLot[]): ImageMatcher[] => {
  return lots.map(lot => ({
    folder: lot.notes || '',
    sku: lot.sku,
    expectedFilenames: generateImageFilenames(lot.sku),
  }));
};